import { useContext, useEffect, useState } from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';

import config from '../../../../config';
import { AppContext } from '../../../../App.context';
import { AuthCognito } from '../../../../config.type';
import {
	REFRESH_TOKEN_KEY,
	parseJwt,
	ACCESS_TOKEN_EXPIRATION_KEY,
	IS_SUBSCRIBED_KEY,
	FEATURES_KEY,
} from '../../../../views/home/auth/Authentication.hooks';
import { ACCESS_TOKEN_KEY } from '../../../../constants';

export function useSuccess(): SuccessResponse {
	const navigate = useNavigate();
	const basename = useHref('/callback');
	const { kv, rebuildGraphQLClient } = useContext(AppContext);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubscribed, setIsSubscribed] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const token = localStorage.getItem(REFRESH_TOKEN_KEY);
	useEffect(() => {
		if (config?.app?.auth?.type !== 'cognito') {
			navigate('/');
			return;
		}
		if (!token) {
			redirectToLogin(basename);
			return;
		}

		kv.clear();

		refreshAccessToken(token)
			.then(() => {
				rebuildGraphQLClient();
				let subscribed: any = localStorage.getItem(IS_SUBSCRIBED_KEY);
				if (subscribed) {
					subscribed = JSON.parse(subscribed);
					setIsSubscribed(subscribed);
				} else {
					localStorage.setItem(FEATURES_KEY, '[]');
				}

				setError(null);
				setIsLoading(false);
				// navigate('/');
			})
			.catch((e) => {
				setIsLoading(false);
				setError(e);
			});
	}, []);

	return {
		isLoading,
		error,
		isSubscribed,
	};
}

interface SuccessResponse {
	isLoading: boolean;
	error: Error | null;
	isSubscribed: boolean;
}

function redirectToLogin(basename: string) {
	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	location.href = redirectUrl;
}

async function refreshAccessToken(token: string) {
	const queryParams = new URLSearchParams({
		refresh_token: token,
	}).toString();
	const url = `${config.build.host}/auth/token?${queryParams}`;
	const rawResponse = await fetch(url, {
		method: 'GET',
		headers: {
			'app-key': config.build.appKey,
			'Content-Type': 'application/json',
		},
	});
	const content = await rawResponse.json();
	const { access_token } = content;

	// JWT Parse Data
	const accessTokenData = parseJwt(access_token);

	clearTokenData();

	// Calculate expiration dates
	const accessExpirationDate = DateTime.fromSeconds(
		accessTokenData.exp,
	).toISO();

	localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, accessExpirationDate);

	if (accessTokenData.entitlements) {
		localStorage.setItem(IS_SUBSCRIBED_KEY, 'true');
		if (accessTokenData.entitlements.features) {
			localStorage.setItem(
				FEATURES_KEY,
				JSON.stringify(accessTokenData.entitlements.features),
			);
		}
	}
}

function clearTokenData() {
	const keys = [
		ACCESS_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRATION_KEY,
		IS_SUBSCRIBED_KEY,
		FEATURES_KEY,
	];

	for (const key of keys) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}
}
